import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useMetaTags } from '../../hooks/useMetaTags';
import { 
  TransactionData, 
  retrieveTransactionData, 
  DEFAULT_VALUES,
  getTransactionKey,
  isAuthenticationFailure,
  hasSessionIdInUrl,
  cleanSessionIdFromUrl,
  usePaymentUtils
} from '../../utils/paymentUtils';
import { api } from '../../utils/apiClient';
import { emailConfig } from '../../config/emailConfig';

/**
 * PurchaseResult component displays the outcome of a token purchase
 * It shows different content based on whether the purchase was successful or not
 * 
 * The data can come from three sources:
 * 1. Direct navigation from checkout (via localStorage/sessionStorage)
 * 2. Regular React Router navigation with state
 * 3. Stripe redirect with session ID in URL
 */
function PurchaseResult() {
  const { t } = useTranslation();
  const { user, refreshTokenBalance } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasSessionId, cleanSessionId, getTransactionData } = usePaymentUtils();
  
  // Transaction result state
  const [resultState, setResultState] = useState<TransactionData | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  
  /**
   * Attempt to refresh token balance with multiple retries
   * @param maxAttempts Maximum number of retry attempts
   * @param baseDelay Base delay in ms between attempts
   * @returns Promise resolving to true if balance was updated
   */
  const refreshBalanceWithRetry = useCallback(async (maxAttempts = 5, baseDelay = 1000): Promise<boolean> => {
    let balanceUpdated = false;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`Refreshing token balance attempt ${attempt}/${maxAttempts}`);
      
      balanceUpdated = await refreshTokenBalance();
      
      if (balanceUpdated) {
        console.log(`Token balance updated successfully on attempt ${attempt}`);
        return true;
      }
      
      if (attempt < maxAttempts) {
        const waitTime = baseDelay * attempt;
        console.log(`Token balance not updated yet, waiting ${waitTime}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
    
    console.warn('Failed to update token balance after multiple attempts');
    return false;
  }, [refreshTokenBalance]);

  // Poll for session status
  const pollSessionStatus = useCallback(async (sessionId: string) => {
    setIsPolling(true);
    
    try {
      console.log(`Polling for session status: ${sessionId}`);
      const result = await api.wallet.getSessionStatus(sessionId);
      
      if (result.success) {
        console.log(`Session status received: ${result.status}, payment_status: ${result.payment_status}`);
        
        // If session is complete, create transaction data
        if (result.status === 'complete') {
          const success = result.payment_status === 'paid';
          
          // If payment was successful, refresh the token balance
          if (success) {
            console.log('Payment successful, refreshing token balance...');
            await refreshBalanceWithRetry();
            
            // Add a short delay to ensure balance is updated before showing the result
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // Create transaction data
          const transactionData: TransactionData = {
            success,
            sessionId,
            paymentIntentId: result.payment_intent,
            tokenAmount: result.tokensAdded || DEFAULT_VALUES.TOKEN_AMOUNT,
            price: result.amount ? result.amount / 100 : DEFAULT_VALUES.PRICE_AMOUNT,
            date: new Date().toISOString(),
            payment_status: result.payment_status,
            status: success ? 'completed' : 'failed',
            error: !success ? 
              (result.payment_status === 'unpaid' ? 
                'Authentication was not completed successfully. Your card has not been charged.' :
                'Payment processing failed. Please try again.') : 
              undefined
          };
          
          // Store and set the result
          setResultState(transactionData);
          setIsPolling(false);
          
          // Clean the URL after we have the result
          cleanSessionId();
          return;
        }
        
        // Continue polling if session is not complete
        setTimeout(() => pollSessionStatus(sessionId), 2000);
      } else {
        console.error('Error polling session:', result.error);
        
        // Create error transaction data
        const transactionData: TransactionData = {
          success: false,
          sessionId,
          tokenAmount: DEFAULT_VALUES.TOKEN_AMOUNT,
          price: DEFAULT_VALUES.PRICE_AMOUNT,
          date: new Date().toISOString(),
          error: result.message || 'Unable to retrieve payment status'
        };
        
        setResultState(transactionData);
        setIsPolling(false);
        cleanSessionIdFromUrl();
      }
    } catch (error) {
      console.error('Polling error:', error);
      
      // Create error transaction data on exception
      const transactionData: TransactionData = {
        success: false,
        sessionId,
        tokenAmount: DEFAULT_VALUES.TOKEN_AMOUNT,
        price: DEFAULT_VALUES.PRICE_AMOUNT,
        date: new Date().toISOString(),
        error: 'An unexpected error occurred'
      };
      
      setResultState(transactionData);
      setIsPolling(false);
      cleanSessionIdFromUrl();
    }
  }, [refreshBalanceWithRetry, cleanSessionId]);
  
  // On mount, check if we have transaction data
  useEffect(() => {
    // Skip if we already have result state or are polling
    if (resultState || isPolling) {
      return;
    }
    
    console.log('PurchaseResult: Initializing, checking for transaction data');
    
    // 1. Check for session_id in URL using consolidated extraction function
    const sessionId = hasSessionId();
    if (sessionId) {
      console.log(`PurchaseResult: Found session_id in URL: ${sessionId}`);
      // Start polling for status
      pollSessionStatus(sessionId);
      return;
    }
    
    // 2. Try to get data from location state (React Router navigation)
    let data: TransactionData | null = location.state as TransactionData;
    
    // 3. If no state, try to get from storage (previous navigation)
    if (!data) {
      try {
        console.log('PurchaseResult: No location state, checking storage');
        data = getTransactionData();
        
        if (data) {
          console.log('PurchaseResult: Found data in storage');
        } else {
          console.log('PurchaseResult: No transaction data found in storage');
        }
      } catch (e) {
        console.error('PurchaseResult: Error retrieving transaction data', e);
      }
    } else {
      console.log('PurchaseResult: Using data from location state');
    }
    
    // If we have data, set it and don't redirect
    if (data) {
      setResultState(data);
    } else {
      // No transaction data found, redirect to wallet
      console.log('PurchaseResult: No transaction data found, redirecting to wallet');
      navigate('/wallet');
    }
  }, [location.state, navigate, resultState, isPolling, pollSessionStatus, hasSessionId, getTransactionData, cleanSessionId]);
  
  // Format a date in user's locale
  const formatDate = (dateStr?: string) => {
    const date = dateStr ? new Date(dateStr) : new Date();
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  
  // Format a time in user's locale
  const formatTime = (dateStr?: string) => {
    const date = dateStr ? new Date(dateStr) : new Date();
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  // If we don't have result state yet, show loading
  if (!resultState) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
          </div>
        </div>
      </div>
    );
  }
  
  // Use the state we have
  const result = resultState;
  const formattedDate = formatDate(result.date);
  const formattedTime = formatTime(result.date);
  
  // Check if this is an authentication failure result using helper
  const isAuthFailure = isAuthenticationFailure(result);
  
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          {/* Result Card */}
          <div className={`card border ${result.success ? 'border-success' : 'border-danger'} shadow-sm`}>
            {/* Header */}
            <div className={`card-header ${result.success ? 'text-bg-success' : 'text-bg-danger'} bg-opacity-75 border-0`}>
              <div className="d-flex justify-content-between align-items-center">
                <h1 className="h3 mb-0">
                  {result.success
                    ? t('payment.purchaseSuccess', 'Purchase Successful')
                    : isAuthFailure 
                      ? t('payment.authenticationFailed', 'Authentication Failed')
                      : t('payment.purchaseFailed', 'Purchase Unsuccessful')}
                </h1>
                <div className="rounded-circle p-2 bg-white bg-opacity-25">
                  <i className={`bi ${result.success ? 'bi-check-lg' : 'bi-x-lg'} fs-3`}></i>
                </div>
              </div>
            </div>
            
            {/* Body */}
            <div className="card-body p-4">
              {/* Date and Reference */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="mb-3 mb-md-0">
                    <p className="text-body-secondary mb-1">{t('payment.date', 'Date')}</p>
                    <p className="fw-bold mb-0">{formattedDate} <span className="fw-normal text-body-secondary">at {formattedTime}</span></p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div>
                    <p className="text-body-secondary mb-1">{t('payment.reference', 'Payment ID')}</p>
                    <p className="fw-bold mb-0">
                      {result.paymentIntentId 
                        ? <span className="d-inline-block">{result.paymentIntentId}</span>
                        : result.sessionId 
                          ? <span className="d-inline-block">{result.sessionId}</span> 
                          : t('payment.notAvailable', 'Not available')}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Success Details */}
              {result.success && (
                <>
                  <hr className="my-4" />
                  
                  {/* Purchase Details */}
                  <div className="mb-4">
                    <h5 className="mb-3">{t('payment.purchaseDetails', 'Purchase Details')}</h5>
                    <div className="bg-body-tertiary rounded-3 p-3">
                      <div className="row align-items-center">
                        <div className="col-6">
                          <span className="text-body-secondary">{t('payment.tokensPurchased', 'Tokens Purchased')}</span>
                        </div>
                        <div className="col-6 text-end">
                          <span className="fw-bold">{result.tokenAmount?.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      <div className="row align-items-center mt-2">
                        <div className="col-6">
                          <span className="text-body-secondary">{t('payment.pricePaid', 'Price Paid')}</span>
                        </div>
                        <div className="col-6 text-end">
                          <span className="fw-bold">€{result.price?.toFixed(2)}</span>
                        </div>
                      </div>
                      
                      <div className="row align-items-center mt-2">
                        <div className="col-6">
                          <span className="text-body-secondary">{t('payment.paymentMethod', 'Payment Method')}</span>
                        </div>
                        <div className="col-6 text-end">
                          <span>{t('payment.creditCard', 'Credit Card')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Current Balance */}
                  <div className="bg-primary bg-opacity-10 border border-primary-subtle rounded-3 p-3 mb-4">
                    <div className="row align-items-center">
                      <div className="col-7">
                        <span className="fs-5">{t('payment.currentBalance', 'Current Balance')}</span>
                      </div>
                      <div className="col-5 text-end">
                        <span className="fs-5 fw-bold">{user.tokenBalance.toLocaleString()} {t('settings.tokens', 'Tokens')}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Success Message */}
                  <div className="alert alert-success border-success mb-0">
                    <div className="d-flex">
                      <div className="me-3">
                        <i className="bi bi-info-circle-fill fs-4"></i>
                      </div>
                      <div>
                        <h6 className="alert-heading">{t('payment.thankYou', 'Thank you for your purchase!')}</h6>
                        <p className="mb-0">
                          {t('payment.successMessage', 'Your tokens have been added to your account. You can now use them for your counters.')}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Error Details */}
              {!result.success && (
                <>
                  <hr className="my-4" />
                  
                  {/* Error Message */}
                  <div className="alert alert-danger border-danger mb-4">
                    <div className="d-flex">
                      <div className="me-3">
                        <i className="bi bi-exclamation-triangle-fill fs-4"></i>
                      </div>
                      <div>
                        <h6 className="alert-heading">
                          {isAuthFailure
                            ? t('payment.authFailedHeading', 'Card authentication failed')
                            : t('payment.paymentFailed', 'Payment could not be processed')}
                        </h6>
                        <p className="mb-0">
                          {isAuthFailure 
                            ? t('payment.authFailedMessage', 'The authentication step for your card was not completed successfully. Your card has NOT been charged.')
                            : (result.error || t('payment.genericError', 'There was a problem processing your payment. Please try again or contact support if the issue persists.'))}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* What to do next */}
                  <div className="bg-body-tertiary rounded-3 p-3 mb-4">
                    <h6 className="mb-3">{t('payment.whatNext', 'What to do next:')}</h6>
                    <ul className="mb-0">
                      <li className="mb-2">{t('payment.tryAgain', 'Try the purchase again')}</li>
                      <li className="mb-2">
                        {isAuthFailure
                          ? t('payment.ensureAuthStep', 'Ensure you complete the authentication step with your bank')
                          : t('payment.checkCard', 'Verify your card information is correct')}
                      </li>
                      <li className="mb-2">{t('payment.contactSupport', 'Contact support if you need assistance')}</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
            
            {/* Footer with Actions */}
            <div className="card-footer bg-transparent p-4 border-0">
              <div className="row">
                <div className="col-sm-6 mb-3 mb-sm-0">
                  <Link to="/wallet" className="btn btn-outline-primary w-100">
                    <i className="bi bi-wallet2 me-2"></i>
                    {t('payment.backToWallet', 'Back to Wallet')}
                  </Link>
                </div>
                <div className="col-sm-6">
                  {result.success ? (
                    <Link to="/purchase-history" className="btn btn-primary w-100">
                      <i className="bi bi-clock-history me-2"></i>
                      {t('payment.viewHistory', 'View Purchase History')}
                    </Link>
                  ) : (
                    <button
                      onClick={() => navigate('/wallet')}
                      className="btn btn-primary w-100"
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      {t('payment.tryAgain', 'Try Again')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Support Information */}
          <div className="card mt-4 border-0 bg-body-tertiary">
            <div className="card-body text-center">
              <p className="text-muted mb-4">
                {t('purchase.failureHelp', 'If you need assistance, please contact our support team:')}
              </p>
              <p className="mb-3">
                <a href={`mailto:${emailConfig.supportEmail}`} className="text-decoration-none">
                  <i className="bi bi-envelope me-2"></i>
                  {emailConfig.supportEmail}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchaseResult; 